import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IDatasource, IGetRowsParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { take } from 'rxjs/operators';
import { GridData } from 'src/app/interfaces/gridData';
import { environment } from 'src/environments/environment';
import { gridInfo } from '../../interfaces/gridData';
import { ProjectAndTypesService } from '../projectandTypes/project-and-types.service';
import { SelectsService } from '../selectService/selects.service';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class GridDatasourceService implements IDatasource {
  private http = inject(HttpClient);
  private projectAndTypes = inject(ProjectAndTypesService);
  private selectsService = inject(SelectsService);

  private cursor: string = '';
  private draw: number = 0;

  constructor() { }

  resetDrawAndCursor() {
    this.cursor = '';
    this.draw = 0;
  }

  createParametres() {
    const { boxId, folderId, project, service, type, dateStart, dateEnd } =
      this.selectsService.fieldsValue();

    const httpParams: { [key: string]: any } = {
      project: this.projectAndTypes.getProjectId(project!) ?? '',
      folderIdSearch: folderId ?? '',
      boxIdSearch: boxId ?? '',
      typeSearch: this.projectAndTypes.getTypeId(type!) ?? '',
      serviceSearch: this.projectAndTypes.getServiceId(service!) ?? '',
      dateStartSearch: dateStart ? DateTime.fromJSDate(dateStart as Date).toUnixInteger() : '',
      dateEndSearch: dateEnd ? DateTime.fromJSDate(dateEnd as Date).toUnixInteger() : '',
      startCursor: this.cursor,
      paginationSize: environment.paginationSize,
      draw: this.draw,
    };

    return new HttpParams({ fromObject: httpParams });
  }

  filterMessage(res: GridData, message: string) {
    res.data = res.data.filter((entry: any) => {
      const rawValue = typeof entry.Message === 'object' ? entry.Message?.value : entry.Message;
      const value = (rawValue || '').toString().toLowerCase();
      const subject = (entry.Subject || '').toString().toLowerCase();
      
      const searchTerm = message.toLowerCase().trim();
      
      return value.includes(searchTerm) || subject.includes(searchTerm);
    });
  }

  formatDateData(res: GridData) {
    res.data.forEach((item: gridInfo) => {
      item.DateTime = DateTime.fromSeconds(+item.DateTime).toLocaleString(DateTime.DATETIME_MED);
    });
  }

  getRows(params: IGetRowsParams) {
    const { startRow, endRow } = params;

    // Reset cursor if this is a new search (starting from row 0)
    if (startRow === 0) {
      this.cursor = '';
    }

    const httpParams = this.createParametres();

    this.http
      .get<GridData>(environment.url + 'ag-messages', { params: httpParams })
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          this.cursor = res.cursor;

          const { message } = this.selectsService.fieldsValue();

          // Filter data if message exists and is not empty
          if (message && message.trim() !== '') {
            this.filterMessage(res, message);
          }

          // Show/hide overlay based on data availability
          res.data.length === 0
            ? params.context.apiRef?.showNoRowsOverlay()
            : params.context.apiRef?.hideOverlay();

          // Format date data
          this.formatDateData(res);

          // Deselect all rows
          params.context.apiRef?.deselectAll();

          // Calculate lastRow for infinite scroll
          let lastRow = -1;

          const receivedDataCount = res.data.length;

          // If we received fewer rows than the expected pagination size,
          // this means we've reached the end of the data
          if (receivedDataCount < environment.paginationSize) {
            lastRow = startRow + receivedDataCount;
          }

          // Increment draw counter for next request
          this.draw++;

          params.successCallback(res.data, lastRow);
        },
        error: (err: any) => {
          console.error('Error fetching grid data:', err);

          let message = "Unable to connect to the server.";

          if (err.status === 0) {
            message = "Network/CORS Error: The server did not respond or blocked the request.";
          } 
          else if (err.status) {
            const statusCode = err.status;

            if (err.error && typeof err.error === 'object' && err.error.message) {
              message = `[Error ${statusCode}] ${err.error.message}`;
            } 
            else if (typeof err.error === 'string') {
              if (err.error.includes('<html')) {
                // Extrae texto limpio si el servidor escupe una página HTML entera
                const parser = new DOMParser();
                const htmlDoc = parser.parseFromString(err.error, 'text/html');
                const cleanMessage = htmlDoc.querySelector('h2')?.textContent 
                                  || htmlDoc.body?.textContent 
                                  || err.statusText;
                                  
                message = `[Error ${statusCode}] ${cleanMessage.trim()}`;
              } else {
                message = `[Error ${statusCode}] ${err.error}`;
              }
            } 
            else {
              message = `[Error ${statusCode}] ${err.statusText || 'Server Error'}`;
            }
          }

          // Disparar la alerta visual
          Swal.fire({
            icon: 'error',
            title: `[Error ${err.status || 'Red'}]`,
            text: message,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Ok',
            customClass: {
              popup: 'animated fadeInDown'
            }
          });

          params.failCallback();
        },
      });
  }
}